import Box from "@mui/material/Box";
import agent from "@root/agent";
import InfoBox from "@root/components/InfoBox";
import MultiReactSelect from "@root/components/ui/multiReactSelect";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Input } from "@root/components/v3/input";
import "./style.scss";
import Tooltip from "@root/components/v2/ui-elements/Tooltip";
import InfoSvg from '@root/assets/svg/Status/InfoSvg';

const customSelect = {
  control: (baseStyles) => ({
    ...baseStyles,

    borderRadius: "5px",
    padding: "4px",
    border: "1px solid var(--border-color-light)",
    boxShadow: "none",
    color: "#484D56",
    fontSize: "14px",
    fontFamily: "Manrope",
    fontWeight: 500,
  }),
  menu: (baseStyles) => ({
    ...baseStyles,
    borderRadius: "5px",
    padding: "10px",

    color: "#484D56",
    fontSize: "14px",
    fontFamily: "Manrope",
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    color: "#767980",
    background: state.isFocused ? "#EDF2F7" : "#FFF",

    fontSize: "14px",
    fontFamily: "Manrope",
    ":active": {
      ...baseStyles[":active"],
      backgroundColor: state.isSelected ? "#FFF" : "#EDF2F9",
    },
  }),
  indicatorSeparator: () => ({}),
  input: (baseStyles) => ({
    ...baseStyles,
    color: "#484D56",
    fontSize: "14px",
    fontFamily: "Manrope",
    fontWeight: 500,
  }),
};

const SetupForm = ({
  isEditMode,
  currentSetup,
  environments,
  META,
  setDisableNext,
  setCurrentSetup,
  setupErrorMessages,
  canShowSetupErrors,
  isPolicyNameAlreadyExisting,
}) => {
  const selectedEnvironment = useMemo(() => {
    if (!currentSetup.envId) return;

    return environments.find((item) => item.id === currentSetup.envId.value);
  }, [currentSetup.envId, environments]);

  useEffect(() => {
    if (
      META?.data.environments.filter(
        (item) => item.enviromentId === currentSetup.envId?.value.toString()
      )[0] &&
      !META?.data.environments.filter(
        (item) => item.enviromentId === currentSetup.envId?.value.toString()
      )[0]?.autoRemediation &&
      !META?.data.environments.filter(
        (item) => item.enviromentId === currentSetup.envId?.value.toString()
      )[0]?.oneClickRemediation
    ) {
      setDisableNext(true);
    } else {
      setDisableNext(false);
    }
  }, [currentSetup, META, setDisableNext]);

  const { data: scanPresent, isFetching: isFetchingScanPresent } = useQuery(
    [`scan_present`],
    () => agent.Cspa.scan_present()
  );

  // Validate if selected environment requires OCR to be enabled.
  useEffect(() => {
    if (selectedEnvironment) {
      if (
        selectedEnvironment &&
        selectedEnvironment.oneClickRemediation &&
        (!selectedEnvironment.lastScanTime ||
          selectedEnvironment.scanStatus !== "complete")
      ) {
        setCurrentSetup((currentConfig) => ({
          ...currentConfig,
          ocrSetupRequired: true,
        }));
      } else {
        setCurrentSetup((currentConfig) => ({
          ...currentConfig,
          ocrSetupRequired: false,
        }));
      }
    }
  }, [selectedEnvironment, setCurrentSetup]);

  return (
    <div id="setupForm" style={{ width: "100%" }}>
      <div>
        <div
          style={{
            color: "#1A202C",
            fontSize: "14px",
            fontFamily: "Manrope",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          Policy Name<span className="text-red-500">*</span>{" "}
          <Tooltip title={"Enter a unique policy name"}>
            <InfoSvg />{" "}
          </Tooltip>
        </div>
        <Input
          className="w-full mt-[10px]"
          value={currentSetup.policyName ?? ""}
          onChange={(e) => {
            e.persist();
            setCurrentSetup((currentConfig) => ({
              ...currentConfig,
              policyName: e.target.value,
            }));
          }}
          type="text"
          placeholder="Enter Policy Name"
        />

        {canShowSetupErrors && setupErrorMessages.nameError ? (
          <Box color="#f00">{setupErrorMessages.nameError}</Box>
        ) : null}

        {isPolicyNameAlreadyExisting ? (
          <Box color="#f00">Name already in use.</Box>
        ) : null}
      </div>
      <div>
        <div
          style={{
            color: "#1A202C",
            fontSize: "14px",
            fontFamily: "Manrope",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: "4px",
            marginBottom: "10px",
          }}
        >
          Environment ID<span className="text-red-500">*</span>{" "}
          <Tooltip title={"Select single environment ID"}>
            <InfoSvg />{" "}
          </Tooltip>
        </div>
        <MultiReactSelect
          disabled={isEditMode}
          allowSelectAll={false}
          name="env_select"
          value={currentSetup.envId}
          onChange={(e) => {
            setCurrentSetup((currentConfig) => ({
              ...currentConfig,
              envId: e,
            }));
          }}
          inputProps={{
            id: "env_select",
          }}
          className="env_select"
          placeholder="Select Environment"
          options={environments.map((env) => ({
            label: env.id,
            value: env.id,
            data: env,
          }))}
          styles={customSelect}
        />

        {!canShowSetupErrors && setupErrorMessages.environmentIdError ? (
          <Box color="#f00">{setupErrorMessages.environmentIdError}</Box>
        ) : null}
      </div>
      {/* {selectedEnvironment &&
      selectedEnvironment.ocrEnabledInContract &&
      selectedEnvironment.ocrEnabledInEnvironment &&
      !selectedEnvironment.lastCompletedScan ? (
        <InfoBox
          message="Please run a full scan and then proceed with policy creation."
          style={{ marginTop: "24px" }}
        />
      ) : selectedEnvironment &&
        selectedEnvironment.ocrEnabledInContract &&
        !selectedEnvironment.ocrEnabledInEnvironment &&
        selectedEnvironment.lastCompletedScan ? (
        <InfoBox
          message="Please enable Automated remediation module for the selected environment then proceed with policy creation."
          style={{ marginTop: "24px" }}
        />
      ) : selectedEnvironment &&
        (!selectedEnvironment.ocrEnabledInContract ||
          !selectedEnvironment.lastCompletedScan) ? (
        <InfoBox
          message="Please enable Automated remediation module and run a full scan for the selected environment and then proceed with policy creation."
          style={{ marginTop: "24px" }}
        />
      ) : null} */}
      {META?.data.environments.filter(
        (item) => item.enviromentId === currentSetup.envId?.value.toString()
      )[0] &&
      !META?.data.environments.filter(
        (item) => item.enviromentId === currentSetup.envId?.value.toString()
      )[0]?.autoRemediation &&
      !META?.data.environments.filter(
        (item) => item.enviromentId === currentSetup.envId?.value.toString()
      )[0]?.oneClickRemediation ? (
        <InfoBox
          message='Please enable "Automated Remediation" or "One Click Remediation" module for the selected environment then proceed with policy creation.'
          style={{ marginTop: "24px" }}
        />
      ) : null}
      {!scanPresent?.is_scan && !isFetchingScanPresent ? (
        <InfoBox
          message="Please run a full scan and then proceed with policy creation."
          style={{ marginTop: "24px" }}
        />
      ) : null}
    </div>
  );
};

export default SetupForm;
